import React, { useState, useEffect, useContext } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import Select from "react-select"
import {
  Button,
  Col,
  Container,
  Form,
  Input,
  Row,
  Nav,
  NavItem,
  NavLink,
  Card,
  CardBody,
  Spinner,
} from "reactstrap"
import { classApi } from "apis/ClassListApi"
import { categoryApi } from "apis/CategoryApi"
import { SessionContext } from "context/sessionContext"

const selectStyles = {
  menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 9999 }),
  menuList: provided => ({
    ...provided,
    backgroundColor: "#fff",
    maxHeight: "200px",
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
    color: "#000",
    cursor: "pointer",
  }),
}

// Evaluation type options — same as CategoryMaster's "marking" dropdown
const evaluationTypeOptions = [
  { value: "Marks Based", label: "Marks Based" },
  { value: "Grade Based", label: "Grade Based" },
]

// Grading style options — replaces radio buttons
const gradingTypeOptions = [
  { value: "Subject Wise", label: "Subject Wise" },
  { value: "Subject + Topic Wise", label: "Subject + Topic Wise" },
]

const CategoryUpdate = () => {
  document.title = "Update Category"
  const navigate = useNavigate()
  const location = useLocation()
  const { Session } = useContext(SessionContext)
  const session = Session || sessionStorage.getItem("SessionId")

  const cat = location.state?.category

  // ── Top-level fields ────────────────────────────────────────────────────────
  const [categoryName, setCategoryName] = useState("")
  const [selectedClasses, setSelectedClasses] = useState([])
  const [evaluationType, setEvaluationType] = useState(null) // Marks Based / Grade Based
  const [gradingType, setGradingType] = useState(null) // Subject Wise / Subject + Topic Wise

  // Tabs shown in the card below
  const [activeTab, setActiveTab] = useState("subject")

  // ── Subject list ────────────────────────────────────────────────────────────
  const [subjects, setSubjects] = useState([])

  // ── Topic list ──────────────────────────────────────────────────────────────
  const [topics, setTopics] = useState([])

  // ── Dropdown options from API ───────────────────────────────────────────────
  const [classOptions, setClassOptions] = useState([])
  const [subjectDropdownOptions, setSubjectDropdownOptions] = useState([])

  // ── Loading state ───────────────────────────────────────────────────────────
  const [subjectsLoading, setSubjectsLoading] = useState(false)

  // ── Subject type (Scholastic / Co-Scholastic) ───────────────────────────────
  const subjectTypeOptions = [
    { value: "scholastic", label: "Scholastic" },
    { value: "co-scholastic", label: "Co-Scholastic" },
  ]
  const [subjectType, setSubjectType] = useState({
    value: "scholastic",
    label: "Scholastic",
  })
  const [coScholasticSubjectName, setCoScholasticSubjectName] = useState("")

  // ── Add-subject inputs ──────────────────────────────────────────────────────
  const [selectedSubject, setSelectedSubject] = useState(null)
  const [subjectSequence, setSubjectSequence] = useState("")
  const [subjectError, setSubjectError] = useState("")

  // ── Add-topic inputs ────────────────────────────────────────────────────────
  const [topicSelectedSubject, setTopicSelectedSubject] = useState(null)
  const [topicName, setTopicName] = useState("")
  const [topicSequence, setTopicSequence] = useState("")
  const [topicError, setTopicError] = useState("")

  const [submitting, setSubmitting] = useState(false)

  const isTopicWise = gradingType?.value === "Subject + Topic Wise"

  // ── Helper: determine subject_type for API payload ──────────────────────────
  const getSubjectType = item => {
    const isCoSch =
      item.subject_code === "" || String(item.subject_code).startsWith("CO")
    return isCoSch ? "CO-SCHOLISTIC" : "SCHOLISTIC"
  }

  // ── Load class options on mount ─────────────────────────────────────────────
  useEffect(() => {
    classApi
      .getAllClass()
      .then(res => {
        const classes = res.data?.class || []
        setClassOptions(
          classes.map(item => ({
            value: item.st_class,
            label: item.st_class.toUpperCase(),
            uid: item.uid,
          }))
        )
      })
      .catch(err => console.error("Failed to load classes:", err))
  }, [])

  // ── Fetch subjects whenever selectedClasses changes ──────────────────────
  useEffect(() => {
    if (!selectedClasses || selectedClasses.length === 0) {
      setSubjectDropdownOptions([])
      setSelectedSubject(null)
      setSubjectError("")
      return
    }

    const classIdentifiers = selectedClasses.map(c => c.value)
    setSubjectsLoading(true)

    classApi
      .getSubjectByClass({ st_class: classIdentifiers })
      .then(res => {
        const fetchedSubjects = res.data?.subjects || []
        const options = fetchedSubjects.map(item => ({
          value: item.subject_code,
          label: item.subject_name,
          subject_code: item.subject_code,
        }))
        setSubjectDropdownOptions(options)

        // Keep co-scholastic subjects (code starts with "CO" or empty string) — they are manually
        // entered and not tied to the class subject list
        const availableCodes = new Set(options.map(o => o.value))
        setSubjects(prev =>
          prev.filter(
            s =>
              s.subject_code === "" ||
              String(s.subject_code).startsWith("CO") ||
              availableCodes.has(s.subject.value)
          )
        )
        setTopics(prev =>
          prev.filter(
            t =>
              String(t.subject.value).startsWith("CO") ||
              availableCodes.has(t.subject.value)
          )
        )

        // Clear add-row inputs if current selection was removed
        setSelectedSubject(prev =>
          prev && availableCodes.has(prev.value) ? prev : null
        )
        setTopicSelectedSubject(prev =>
          prev && availableCodes.has(prev.value) ? prev : null
        )

        setSubjectError("")
        setTopicError("")
      })
      .catch(err => {
        console.error("Failed to fetch subjects by class:", err)
        setSubjectDropdownOptions([])
      })
      .finally(() => {
        setSubjectsLoading(false)
      })
  }, [selectedClasses])

  // ── Pre-populate from passed category ──────────────────────────────────────
  useEffect(() => {
    if (!cat || classOptions.length === 0) return

    setCategoryName(cat.category_name || "")

    // Classes — match against classOptions to get uid
    const rawClasses = Array.isArray(cat.st_class) ? cat.st_class : []
    setSelectedClasses(
      rawClasses.map(item => {
        const name = typeof item === "object" ? item.name : item
        const match = classOptions.find(
          opt => opt.value.toLowerCase() === (name || "").toLowerCase()
        )
        return (
          match || {
            value: name || "",
            label: (name || "").toUpperCase(),
            uid: item?.uid ?? null,
          }
        )
      })
    )

    // Evaluation type (Marks Based / Grade Based)
    const evalVal = cat.evaluation_type || ""
    setEvaluationType(
      evaluationTypeOptions.find(o => o.value === evalVal) || null
    )

    // Grading type (Subject Wise / Subject + Topic Wise)
    const markingVal = cat.marking || ""
    const matchedGrading = gradingTypeOptions.find(o => o.value === markingVal)
    setGradingType(matchedGrading || null)

    setActiveTab("subject")

    // Build subject list
    const loadedSubjects = (cat.subjects || []).map((sub, idx) => ({
      id: sub.id || Date.now() + idx,
      subject: {
        value: sub.subject_code,
        label: sub.subject_name,
        subject_code: sub.subject_code,
      },
      subject_code: sub.subject_code,
      sequence_no: String(sub.sequence_no ?? idx + 1),
    }))
    setSubjects(loadedSubjects)

    // Auto-detect subject type from the loaded subjects:
    // subject_code starting with "CO" or empty → co-scholastic, otherwise scholastic
    const hasCoScholastic = loadedSubjects.some(
      s => s.subject_code === "" || String(s.subject_code).startsWith("CO")
    )
    setSubjectType(
      hasCoScholastic
        ? { value: "co-scholastic", label: "Co-Scholastic" }
        : { value: "scholastic", label: "Scholastic" }
    )

    // Topics — flatten from subjects[].topics
    const existingTopics = []
    ;(cat.subjects || []).forEach(sub => {
      ;(sub.topics || []).forEach((t, tIdx) => {
        existingTopics.push({
          id: t.id || Date.now() + tIdx + Math.random(),
          subject: { value: sub.subject_code, label: sub.subject_name },
          topic: t.topic_name || "",
          sequence_no: String(t.sequence_no ?? tIdx + 1),
        })
      })
    })
    setTopics(existingTopics)
  }, [cat, classOptions])

  // ── Subject helpers ────────────────────────────────────────────────────────
  const addSubject = () => {
    setSubjectError("")

    if (subjectType.value === "scholastic") {
      if (!selectedSubject || !subjectSequence.trim()) {
        setSubjectError("Please select a subject and enter a sequence number.")
        return
      }
      if (subjects.find(s => s.subject.value === selectedSubject.value)) {
        setSubjectError(
          `"${selectedSubject.label}" has already been added. Duplicate subjects are not allowed.`
        )
        return
      }
      if (
        subjects.find(s => String(s.sequence_no) === subjectSequence.trim())
      ) {
        const dup = subjects.find(
          s => String(s.sequence_no) === subjectSequence.trim()
        )
        setSubjectError(
          `Sequence No. "${subjectSequence.trim()}" is already used by "${
            dup.subject.label
          }". Please use a unique sequence number.`
        )
        return
      }
      setSubjects(prev => [
        ...prev,
        {
          id: Date.now(),
          subject: selectedSubject,
          subject_code: selectedSubject.subject_code,
          sequence_no: subjectSequence.trim(),
        },
      ])
      setSelectedSubject(null)
      setSubjectSequence("")
    } else {
      // co-scholastic
      const trimmedName = coScholasticSubjectName.trim().toUpperCase()
      if (!trimmedName || !subjectSequence.trim()) {
        setSubjectError("Please enter both subject name and sequence number.")
        return
      }
      if (subjects.find(s => s.subject.label.toUpperCase() === trimmedName)) {
        setSubjectError(
          `"${trimmedName}" has already been added. Duplicate subjects are not allowed.`
        )
        return
      }
      if (
        subjects.find(s => String(s.sequence_no) === subjectSequence.trim())
      ) {
        const dup = subjects.find(
          s => String(s.sequence_no) === subjectSequence.trim()
        )
        setSubjectError(
          `Sequence No. "${subjectSequence.trim()}" is already used by "${
            dup.subject.label
          }". Please use a unique sequence number.`
        )
        return
      }
      // Generate a temp value for the subject's select option key (not sent to backend)
      const coCode = `CO_SC_${Date.now()}`
      setSubjects(prev => [
        ...prev,
        {
          id: Date.now(),
          subject: { value: coCode, label: trimmedName },
          subject_code: "", // backend expects empty string for co-scholastic
          sequence_no: subjectSequence.trim(),
        },
      ])
      setCoScholasticSubjectName("")
      setSubjectSequence("")
    }
  }

  const removeSubject = id => {
    const removed = subjects.find(s => s.id === id)
    setSubjects(prev => prev.filter(s => s.id !== id))
    if (removed)
      setTopics(prev =>
        prev.filter(t => t.subject.value !== removed.subject.value)
      )
  }

  // ── Topic helpers ──────────────────────────────────────────────────────────
  const addTopic = () => {
    setTopicError("")
    if (!topicSelectedSubject) {
      setTopicError("Please select a subject before adding a topic.")
      return
    }
    if (!topicName.trim() || !topicSequence.trim()) {
      setTopicError("Please enter both topic name and sequence number.")
      return
    }
    if (
      topics.find(
        t =>
          t.subject.value === topicSelectedSubject.value &&
          t.topic.trim().toLowerCase() === topicName.trim().toLowerCase()
      )
    ) {
      setTopicError(
        `Topic "${topicName.trim()}" already exists for this subject.`
      )
      return
    }
    if (
      topics.find(
        t =>
          t.subject.value === topicSelectedSubject.value &&
          String(t.sequence_no) === topicSequence.trim()
      )
    ) {
      setTopicError(
        `Sequence No. "${topicSequence.trim()}" is already used in this subject.`
      )
      return
    }
    setTopics(prev => [
      ...prev,
      {
        id: Date.now(),
        subject: topicSelectedSubject,
        topic: topicName.trim(),
        sequence_no: topicSequence.trim(),
      },
    ])
    setTopicSelectedSubject(null)
    setTopicName("")
    setTopicSequence("")
  }

  const removeTopic = id => setTopics(prev => prev.filter(t => t.id !== id))

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault()

    if (!categoryName.trim()) {
      Swal.fire({
        title: "Validation",
        text: "Category name is required.",
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      return
    }
    if (selectedClasses.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Please select at least one class.",
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      return
    }
    if (!evaluationType) {
      Swal.fire({
        title: "Validation",
        text: "Please select an evaluation type.",
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      return
    }
    if (!gradingType) {
      Swal.fire({
        title: "Validation",
        text: "Please select a grading type.",
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      return
    }
    if (subjects.length === 0) {
      Swal.fire({
        title: "Validation",
        text: "Please add at least one subject.",
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      setActiveTab("subject")
      return
    }

    // Mixed subject type check — cannot mix scholastic and co-scholastic
    const isCoSch = s =>
      s.subject_code === "" || String(s.subject_code).startsWith("CO")
    const hasScholastic = subjects.some(s => !isCoSch(s))
    const hasCoScholastic = subjects.some(s => isCoSch(s))
    if (hasScholastic && hasCoScholastic) {
      Swal.fire({
        title: "Mixed Subject Types",
        html: `You have added both <strong>Scholastic</strong> and <strong>Co-Scholastic</strong> subjects.<br/><br/>
          Please use only <strong>one subject type</strong> — either all Scholastic or all Co-Scholastic subjects.`,
        icon: "warning",
        confirmButtonColor: "#556ee6",
      })
      setActiveTab("subject")
      return
    }

    // Topic-wise validations
    if (isTopicWise) {
      if (topics.length === 0) {
        Swal.fire({
          title: "No Topics Added",
          html: `You have selected <strong>Subject + Topic Wise</strong> grading but have not added topics to any subject.<br/><br/>
            Please add topics to all subjects, or switch to <strong>Subject Wise</strong>.`,
          icon: "warning",
          confirmButtonColor: "#556ee6",
        })
        setActiveTab("topic")
        return
      }

      const subjectsWithoutTopics = subjects.filter(
        sub => !topics.some(t => t.subject.value === sub.subject.value)
      )
      if (subjectsWithoutTopics.length > 0) {
        const missingList = subjectsWithoutTopics
          .map((s, i) => `${i + 1}. ${s.subject.label}`)
          .join("<br/>")
        Swal.fire({
          title: "Topics Missing for Some Subjects",
          html: `You have not added topics for the following subject(s):<br/><br/>
            <strong>${missingList}</strong><br/><br/>
            Please add at least one topic for each subject.`,
          icon: "warning",
          confirmButtonColor: "#556ee6",
        })
        setActiveTab("topic")
        return
      }
    }

    const commonPayload = {
      category_id: cat?.id || "",
      session_id: session ? parseInt(session, 10) : null,
      category_name: categoryName.trim(),
      class: selectedClasses.map(c => ({ uid: c.uid, name: c.value })),
      evaluation_type: evaluationType.value,
    }

    setSubmitting(true)
    try {
      let res
      if (!isTopicWise) {
        res = await categoryApi.addSubjectWiseCategory({
          ...commonPayload,
          marking: "Subject Wise",
          subjects: subjects.map(item => {
            const entry = {
              subject_name: item.subject.label,
              sequence_no: Number(item.sequence_no),
              subject_type: getSubjectType(item),
            }
            // Only include subject_code for existing scholastic subjects
            if (
              item.subject_code &&
              !String(item.subject_code).startsWith("CO_SC_")
            ) {
              entry.subject_code = item.subject_code
            }
            return entry
          }),
        })
      } else {
        res = await categoryApi.addSubjectPlusTopicWiseCategory({
          ...commonPayload,
          marking: "Subject + Topic Wise",
          subjects: subjects.map(sub => {
            const entry = {
              subject_name: sub.subject.label,
              subject_type: getSubjectType(sub),
              topics: topics
                .filter(t => t.subject.value === sub.subject.value)
                .map(t => ({
                  topic_name: t.topic,
                  sequence_no: Number(t.sequence_no),
                })),
            }
            if (
              sub.subject_code &&
              !String(sub.subject_code).startsWith("CO_SC_")
            ) {
              entry.subject_code = sub.subject_code
            }
            return entry
          }),
        })
      }

      const data = res?.data ?? res
      if (data?.status === 200 || data?.status === 201) {
        await Swal.fire({
          title: "Updated!",
          text: data?.msg || "Category updated successfully.",
          icon: "success",
          confirmButtonColor: "#556ee6",
        })
        navigate("/view-categories")
      } else {
        Swal.fire({
          title: "Failed",
          text: data?.msg || "Update failed. Please try again.",
          icon: "error",
          confirmButtonColor: "#556ee6",
        })
      }
    } catch (err) {
      console.error("Update error:", err)
      Swal.fire({
        title: "Error",
        text: err.response?.data?.msg || "An error occurred while updating.",
        icon: "error",
        confirmButtonColor: "#556ee6",
      })
    } finally {
      setSubmitting(false)
    }
  }

  // ── Guard ──────────────────────────────────────────────────────────────────
  if (!cat) {
    return (
      <div className="page-content">
        <div className="container-fluid text-center py-5 text-muted">
          <i className="bx bx-error-circle" style={{ fontSize: "48px" }} />
          <p className="mt-3">
            No category data found. Please go back and click Edit again.
          </p>
          <Button color="primary" onClick={() => navigate("/view-categories")}>
            Back to Categories
          </Button>
        </div>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Container fluid>
            <Breadcrumb title="Examination" breadcrumbItem="Update Category" />

            <Form onSubmit={handleSubmit}>
              {/* ── Row 1: Category Name + Class + Evaluation Type ── */}
              <Row className="mb-3">
                <Col lg={4}>
                  <label>
                    Category Name <span className="text-danger">*</span>
                  </label>
                  <Input
                    type="text"
                    value={categoryName}
                    onChange={e => setCategoryName(e.target.value)}
                    placeholder="Enter Category Name"
                  />
                </Col>

                <Col lg={4}>
                  <label>
                    Class <span className="text-danger">*</span>
                  </label>
                  <Select
                    isMulti
                    classNamePrefix="select2-selection"
                    options={classOptions}
                    placeholder="Select Class(es)"
                    value={selectedClasses}
                    onChange={val => setSelectedClasses(val || [])}
                    styles={selectStyles}
                  />
                </Col>

                <Col lg={4}>
                  <label>
                    Evaluation Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    classNamePrefix="select2-selection"
                    options={evaluationTypeOptions}
                    placeholder="Select Evaluation Type"
                    value={evaluationType}
                    onChange={val => setEvaluationType(val)}
                    styles={selectStyles}
                  />
                </Col>
              </Row>

              {/* ── Row 2: Grading Type + actions ── */}
              <Row className="mb-3 align-items-end">
                <Col lg={4}>
                  <label>
                    Grading Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    classNamePrefix="select2-selection"
                    options={gradingTypeOptions}
                    placeholder="Select Grading Type"
                    value={gradingType}
                    onChange={val => {
                      setGradingType(val)
                      // switching away from topic-wise: reset to subject tab
                      if (val?.value !== "Subject + Topic Wise") {
                        setActiveTab("subject")
                      }
                    }}
                    styles={selectStyles}
                  />
                </Col>

                <Col lg={8} className="d-flex justify-content-end gap-2">
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    onClick={() => navigate("/view-categories")}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" color="success" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Updating...
                      </>
                    ) : (
                      "Update Category"
                    )}
                  </Button>
                </Col>
              </Row>

              {/* ── Subject / Topic card ── */}
              <Card className="mt-2">
                <CardBody>
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        active={activeTab === "subject"}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setActiveTab("subject")
                          setSubjectError("")
                        }}
                      >
                        Add Subject
                      </NavLink>
                    </NavItem>
                    {isTopicWise && (
                      <NavItem>
                        <NavLink
                          active={activeTab === "topic"}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            setActiveTab("topic")
                            setTopicError("")
                          }}
                        >
                          Add Topic
                        </NavLink>
                      </NavItem>
                    )}
                  </Nav>

                  {/* ── Subjects tab ── */}
                  {activeTab === "subject" && (
                    <>
                      {selectedClasses.length === 0 && (
                        <div
                          className="alert alert-warning mt-3 py-2 px-3"
                          style={{ fontSize: "13px" }}
                        >
                          Please select at least one <strong>Class</strong>{" "}
                          above to load the available subjects.
                        </div>
                      )}

                      <Row className="mt-4">
                        {/* Subject Type */}
                        <Col lg={2}>
                          <label>Subject Type *</label>
                          <Select
                            classNamePrefix="select2-selection"
                            options={subjectTypeOptions}
                            value={subjectType}
                            onChange={val => {
                              setSubjectType(val)
                              setSelectedSubject(null)
                              setCoScholasticSubjectName("")
                              setSubjectSequence("")
                              setSubjectError("")
                            }}
                            styles={selectStyles}
                          />
                        </Col>

                        {/* Subject — dropdown for Scholastic, text input for Co-Scholastic */}
                        <Col lg={4}>
                          <label>Subject *</label>
                          {subjectType.value === "scholastic" ? (
                            <Select
                              classNamePrefix="select2-selection"
                              options={subjectDropdownOptions}
                              placeholder={
                                subjectsLoading
                                  ? "Loading subjects..."
                                  : selectedClasses.length === 0
                                  ? "Select a class first"
                                  : "Select Subject"
                              }
                              value={selectedSubject}
                              onChange={val => {
                                setSelectedSubject(val)
                                setSubjectError("")
                              }}
                              isDisabled={
                                subjectsLoading || selectedClasses.length === 0
                              }
                              isLoading={subjectsLoading}
                              styles={selectStyles}
                            />
                          ) : (
                            <Input
                              value={coScholasticSubjectName}
                              placeholder="Enter Subject Name"
                              onChange={e => {
                                setCoScholasticSubjectName(
                                  e.target.value.toUpperCase()
                                )
                                setSubjectError("")
                              }}
                              onKeyDown={e => {
                                if (e.key === "Enter") {
                                  e.preventDefault()
                                  addSubject()
                                }
                              }}
                            />
                          )}
                        </Col>

                        <Col lg={2}>
                          <label>Sequence No *</label>
                          <Input
                            value={subjectSequence}
                            placeholder="Enter Sequence No"
                            disabled={
                              subjectType.value === "scholastic" &&
                              (subjectsLoading || selectedClasses.length === 0)
                            }
                            onChange={e => {
                              setSubjectSequence(e.target.value)
                              setSubjectError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addSubject()
                              }
                            }}
                          />
                        </Col>
                        <Col lg={2} className="d-flex align-items-end">
                          <Button
                            type="button"
                            color="primary"
                            onClick={addSubject}
                            disabled={
                              subjectType.value === "scholastic" &&
                              (subjectsLoading || selectedClasses.length === 0)
                            }
                          >
                            Add
                          </Button>
                        </Col>
                      </Row>

                      {subjectError && (
                        <div
                          className="alert alert-danger py-2 px-3 mt-2 mb-0"
                          style={{ fontSize: "13px" }}
                        >
                          {subjectError}
                        </div>
                      )}

                      <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th width="60">Sr.No</th>
                              <th>Subject</th>
                              {subjectType.value !== "co-scholastic" && <th>Subject Code</th>}
                              <th>Sequence</th>
                              <th width="80">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subjects.length > 0 ? (
                              subjects.map((item, index) => {
                                const rawCode = item.subject_code || item.subject?.subject_code || ""
                                const isCo = rawCode === "" || String(rawCode).startsWith("CO")
                                const displayCode = isCo ? "-" : rawCode
                                return (
                                  <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>{item.subject.label}</td>
                                    {subjectType.value !== "co-scholastic" && <td>{displayCode}</td>}
                                    <td>{item.sequence_no}</td>
                                    <td>
                                      <Button
                                        type="button"
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeSubject(item.id)}
                                      >
                                        <i className="mdi mdi-delete-outline" />
                                      </Button>
                                    </td>
                                  </tr>
                                )
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="text-center text-muted"
                                >
                                  No subjects added yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}

                  {/* ── Topics tab ── */}
                  {activeTab === "topic" && (
                    <>
                      {subjects.length === 0 && (
                        <div
                          className="alert alert-warning mt-3 py-2 px-3"
                          style={{ fontSize: "13px" }}
                        >
                          Please add at least one subject from the{" "}
                          <strong>Add Subject</strong> tab before adding topics.
                        </div>
                      )}

                      <Row className="mt-4">
                        <Col lg={3}>
                          <label>Subject *</label>
                          <Select
                            classNamePrefix="select2-selection"
                            options={subjects.map(s => s.subject)}
                            value={topicSelectedSubject}
                            onChange={val => {
                              setTopicSelectedSubject(val)
                              setTopicError("")
                            }}
                            placeholder="Select Subject"
                            styles={selectStyles}
                            isDisabled={subjects.length === 0}
                          />
                        </Col>
                        <Col lg={3}>
                          <label>Topic Name *</label>
                          <Input
                            value={topicName}
                            placeholder="Enter Topic"
                            onChange={e => {
                              setTopicName(e.target.value)
                              setTopicError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addTopic()
                              }
                            }}
                            disabled={subjects.length === 0}
                          />
                        </Col>
                        <Col lg={2}>
                          <label>Sequence No *</label>
                          <Input
                            value={topicSequence}
                            placeholder="Enter Sequence No"
                            onChange={e => {
                              setTopicSequence(e.target.value)
                              setTopicError("")
                            }}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault()
                                addTopic()
                              }
                            }}
                            disabled={subjects.length === 0}
                          />
                        </Col>
                        <Col lg={2} className="d-flex align-items-end">
                          <Button
                            type="button"
                            color="primary"
                            onClick={addTopic}
                            disabled={subjects.length === 0}
                          >
                            Add
                          </Button>
                        </Col>
                      </Row>

                      {topicError && (
                        <div
                          className="alert alert-danger py-2 px-3 mt-2 mb-0"
                          style={{ fontSize: "13px" }}
                        >
                          {topicError}
                        </div>
                      )}

                      <div className="table-responsive mt-3">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th width="60">Sr.No</th>
                              <th>Subject</th>
                              <th>Topic</th>
                              <th>Sequence</th>
                              <th width="80">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topics.length > 0 ? (
                              topics.map((item, index) => (
                                <tr key={item.id}>
                                  <td>{index + 1}</td>
                                  <td>{item.subject.label}</td>
                                  <td>{item.topic}</td>
                                  <td>{item.sequence_no}</td>
                                  <td>
                                    <Button
                                      type="button"
                                      color="danger"
                                      size="sm"
                                      onClick={() => removeTopic(item.id)}
                                    >
                                      <i className="mdi mdi-delete-outline" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td
                                  colSpan={5}
                                  className="text-center text-muted"
                                >
                                  No topics added yet.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Form>
          </Container>
        </div>
      </div>
    </React.Fragment>
  )
}

export default CategoryUpdate
